// src/utils/useTimechainWebSocket.js

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { throttle } from './throttle';

const PRIMARY_URL = "wss://2140data.io";
const NO_DATA_TIMEOUT = 15000; // 15 seconds

export const useTimechainWebSocket = () => {
  const [bitcoinPrice, setBitcoinPrice] = useState(null);
  const [isDataFlowing, setIsDataFlowing] = useState(false);
  const wsRef = useRef(null);
  const dataTimeoutRef = useRef(null);
  const reconnectIntervalRef = useRef(null);
  
  // Throttle price updates to once per second (1000ms)
  const throttledSetPrice = useMemo(
    () => throttle((price) => {
      setBitcoinPrice(price);
    }, 1000),
    []
  );

  const resetDataTimeout = useCallback(() => {
    if (dataTimeoutRef.current) {
      clearTimeout(dataTimeoutRef.current);
    }
    dataTimeoutRef.current = setTimeout(() => {
      setIsDataFlowing(false);
    }, NO_DATA_TIMEOUT);
  }, []);

  const connectWebSocket = useCallback((url) => {
    // console.log(`Attempting to connect to ${url}`);

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.onclose = null;
      wsRef.current.close(1000, 'Switching connection');
    }

    wsRef.current = new WebSocket(url);

    wsRef.current.onopen = () => {
      // console.log(`WebSocket connected to ${url}`);
      setIsDataFlowing(false);
      resetDataTimeout();
      
      // Timechain sends continuous price data, so we don't need a keep-alive
    };

    wsRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.weightedPrice) {
          const price = parseFloat(data.weightedPrice).toFixed(2);
          throttledSetPrice(price);
          setIsDataFlowing(true);
          resetDataTimeout();
        } else {
          // console.log('Received data does not contain weightedPrice:', data);
        }
      } catch (error) {
        // console.error('Error parsing WebSocket message:', error);
      }
    };

    wsRef.current.onerror = (error) => {
      // console.error(`WebSocket error for ${url}:`, error);
    };

    wsRef.current.onclose = (event) => {
      // console.log(`WebSocket closed for ${url}. Code: ${event.code}, Reason: ${event.reason}`);
      setIsDataFlowing(false);
      
      
      // Don't attempt to reconnect if we're shutting down
      if (event.code === 1000 || event.wasClean) {
        return;
      }
    };
  }, [resetDataTimeout, throttledSetPrice]);

  useEffect(() => {
    // Handle visibility change events (when PWA returns from background)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Clear any existing intervals
        if (reconnectIntervalRef.current) {
          clearInterval(reconnectIntervalRef.current);
          reconnectIntervalRef.current = null;
        }
        
        // Check if WebSocket is closed or closing and reconnect immediately
        if (!wsRef.current || 
            wsRef.current.readyState === WebSocket.CLOSED ||
            wsRef.current.readyState === WebSocket.CLOSING) {
          connectWebSocket(PRIMARY_URL);
        }
      }
    };
    
    // Handle focus events as a fallback
    const handleFocus = () => {
      handleVisibilityChange();
    };
    
    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    connectWebSocket(PRIMARY_URL);

    reconnectIntervalRef.current = setInterval(() => {
      if (!isDataFlowing) {
        connectWebSocket(PRIMARY_URL);
      }
    }, 5000);

    return () => {
      // console.log("Cleaning up WebSocket connections");
      // Remove event listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      
      if (reconnectIntervalRef.current) {
        clearInterval(reconnectIntervalRef.current);
      }
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.onclose = null;
        wsRef.current.close(1000, 'Component unmount');
      }
      if (dataTimeoutRef.current) clearTimeout(dataTimeoutRef.current);
      // Cancel any pending throttled function calls
      throttledSetPrice.cancel();
    };
  }, [connectWebSocket, isDataFlowing, throttledSetPrice]);

  return { bitcoinPrice, isConnected: isDataFlowing };
};

// // temporary coincap fallback
// import { useState, useEffect, useRef, useCallback } from "react";

// const COINCAP_URL = "wss://ws.coincap.io/prices?assets=bitcoin";
// const NO_DATA_TIMEOUT = 15000; // 15 seconds

// export const useTimechainWebSocket = () => {
//   const [bitcoinPrice, setBitcoinPrice] = useState(null);
//   const [isDataFlowing, setIsDataFlowing] = useState(false);
//   const wsRef = useRef(null);
//   const dataTimeoutRef = useRef(null);

//   const resetDataTimeout = useCallback(() => {
//     if (dataTimeoutRef.current) {
//       clearTimeout(dataTimeoutRef.current);
//     }
//     dataTimeoutRef.current = setTimeout(() => {
//       setIsDataFlowing(false);
//       console.log(`No data received for ${NO_DATA_TIMEOUT / 1000} seconds, attempting to reconnect`);
//       // We'll call connectWebSocket from outside this callback
//     }, NO_DATA_TIMEOUT);
//   }, []);

//   const connectWebSocket = useCallback(() => {
//     console.log(`Attempting to connect to ${COINCAP_URL}`);
//     if (wsRef.current) {
//       wsRef.current.onclose = null;
//       wsRef.current.close();
//     }
//     wsRef.current = new WebSocket(COINCAP_URL);

//     wsRef.current.onopen = () => {
//       console.log(`WebSocket connected to ${COINCAP_URL}`);
//       setIsDataFlowing(false);
//       resetDataTimeout();
//     };

//     wsRef.current.onmessage = (event) => {
//       try {
//         const data = JSON.parse(event.data);
//         if (data.bitcoin) {
//           const price = parseFloat(data.bitcoin).toFixed(2);
//           setBitcoinPrice(price);
//           setIsDataFlowing(true);
//           resetDataTimeout();
//         } else {
//           console.log('Received data does not contain bitcoin price:', data);
//         }
//       } catch (error) {
//         console.error('Error parsing WebSocket message:', error);
//       }
//     };

//     wsRef.current.onerror = (error) => {
//       console.error(`WebSocket error for ${COINCAP_URL}:`, error);
//     };

//     wsRef.current.onclose = (event) => {
//       console.log(`WebSocket closed for ${COINCAP_URL}. Code: ${event.code}, Reason: ${event.reason}`);
//       setIsDataFlowing(false);
//     };
//   }, [resetDataTimeout]);

//   useEffect(() => {
//     const attemptConnection = () => {
//       connectWebSocket();
//     };

//     attemptConnection();

//     const intervalId = setInterval(() => {
//       if (!isDataFlowing) {
//         attemptConnection();
//       }
//     }, 5000);

//     return () => {
//       console.log("Cleaning up WebSocket connections");
//       clearInterval(intervalId);
//       if (wsRef.current) {
//         wsRef.current.onclose = null;
//         wsRef.current.close();
//       }
//       if (dataTimeoutRef.current) clearTimeout(dataTimeoutRef.current);
//     };
//   }, [connectWebSocket, isDataFlowing]);

//   return { bitcoinPrice, isConnected: isDataFlowing };
// };